import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import { TournamentItem } from "store/liveOps/tournaments/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import SearchInput from "components/inputs/SearchInput/SearchInput";

import { Modal } from "components/Modal/Modal";
import styles from "./GameActionModal.module.scss";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

const AddToTournamentModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isFetched, setFetched] = useState<boolean>(false);
  const [tournamentsList, setTournamentsList] = useState<TournamentItem[]>([]);
  const [tournamentsListFull, setTournamentsListFull] = useState<
    TournamentItem[]
  >([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedTournament, setSelectedTournament] = useState<number>();
  const selectedItems = useAppSelector((store) => store.games.selectedItems);

  const handleLoadTournaments = async () => {
    setFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.tournamentsList}?limit=1000&offset=0`,
      );
      setTournamentsListFull(result.data.items);
    } catch {
      console.log("handleLoadTournaments error");
    }
    setFetched(true);
  };

  useEffect(() => {
    handleLoadTournaments();
  }, []);

  useEffect(() => {
    setTournamentsList(
      tournamentsListFull.filter((el) =>
        el.title?.toLowerCase().includes(searchValue?.toLowerCase()),
      ),
    );
  }, [tournamentsListFull, searchValue]);

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
  };

  const handleApply = async () => {
    try {
      await instance.put(
        `${apiRoutes.tournamentsAddGames}/${selectedTournament}`,
        {
          gameIds: selectedItems,
        },
      );
      dispatch(
        showNotification({
          title: t("success"),
          message: t("addGamesSuccess"),
          type: NotificationTypes.success,
        }),
      );
      handleCloseClick();
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("addGamesError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const handleItemClick = (id: number) => {
    if (selectedTournament === id) {
      setSelectedTournament(undefined);
    } else {
      setSelectedTournament(id);
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header className={styles.addToTournamentTitle}>
        {t("gamesAddToTournament")}
      </Modal.Header>
      <Modal.Content className={styles.addToLiveOpsEntityContentWrapper}>
        {isFetched ? (
          <div>
            <div className="games-order-search">
              <SearchInput
                placeholder={t("searchTournament")}
                value={searchValue}
                onChange={onSearch}
                isSmall
              />
            </div>
            <div className="games-order-list segments-order-list wagers-order-list">
              {isFetched ? (
                <>
                  <div className="item">
                    <div className="id">ID</div>
                    <div className="name">{t("name").toUpperCase()}</div>
                    <div className="description">
                      {t("description").toUpperCase()}
                    </div>
                    <div className="active">{t("active").toUpperCase()}</div>
                  </div>
                  {tournamentsList.map((el) => (
                    <div
                      className={`item ${selectedTournament === el.id ? "active" : ""}`}
                      key={el.id}
                      onClick={() => {
                        handleItemClick(el.id ?? 0);
                      }}
                    >
                      <div className="id">{el.id}</div>
                      <div className="name">{el.title}</div>
                      <div className="description">{el.description}</div>
                      <div className="active">
                        <div
                          className={`active-label ${el.isActive ? "active" : ""}`}
                        >
                          {el.isActive ? "On" : "Off"}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <ModalTabSkeleton />
              )}
            </div>
          </div>
        ) : (
          <ModalTabSkeleton />
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button isSecondary txt={t("cancel")} handleClick={handleCloseClick} />
        <Button isPrimary handleClick={handleApply} txt={t("save")} />
      </Modal.Footer>
    </Modal>
  );
};

export default AddToTournamentModal;
